import { consoleLog } from "../../consoleLog/consoleLog";
import { getNextDateYYYYMMDD } from "./validateCheckinCheckoutDate";

export const transformHotelData = (data: any[], isThisMultiCityTrip: boolean) => {
  return data
    .map((entry) => {
      const validHotels = isThisMultiCityTrip
        ? entry.hotels
        : entry.hotels.filter(
          (hotel: any) => hotel?.accommodation_location_city && hotel?.accommodation_location_city.trim() !== ""
        );
      if (validHotels.length === 0) return null;

      // Map valid hotels to the required format
      return validHotels.map((hotel: any) => {
        const startDate = entry?.start_date ? new Date(entry?.start_date) : null;
        const endDate = entry?.end_date ? new Date(entry?.end_date) : null;

        // checkIn & checkOut same add one more day
        if (!startDate || isNaN(startDate?.getTime()) || !endDate || isNaN(endDate?.getTime())) {
          console.warn("Invalid start_date or end_date for entry:", entry);
          return null;
        }
        let checkOut = endDate;
        // Add one day to check-out if check-in and check-out are the same
        if (startDate?.getTime() === endDate?.getTime()) {
          checkOut = new Date(endDate);
          checkOut.setDate(checkOut?.getDate() + 1);
        }
        return {
          checkIn: startDate?.toISOString().split("T")[0],
          checkOut: checkOut?.toISOString().split("T")[0],
          country: hotel?.accommodation_location_country_code || "",
          city: hotel?.accommodation_location_city || "",
          latitude: hotel?.accommodation_location_latitude || "",
          longitude: hotel?.accommodation_location_longitude || "",
          accommodation_location_name: hotel?.accommodation_location_name || "",
          cityNotAvailable: false,
        };
      });
    })
    .filter(Boolean)
    .flat();
};

function getNextMonthDate() {
  const today = new Date();
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());

  // Format to YYYY-MM-DD
  const year = nextMonth.getFullYear();
  const month = String(nextMonth.getMonth() + 1).padStart(2, "0"); // Ensure two digits
  const day = String(nextMonth.getDate()).padStart(2, "0"); // Ensure two digits

  return `${year}-${month}-${day}`;
}
function isValidFutureDate(dateStr: string): boolean {
  // Parse input date
  const inputDate = new Date(dateStr);
  // Check if input date is valid
  if (isNaN(inputDate.getTime())) return false;

  // Get today's date without time
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Remove time part

  // Return true if input date is today or a future date
  return inputDate >= today;
}

function parseDate(dateStr: any) {
  // dateStr expected in 'YYYY-MM-DD' format
  const [year, month, day] = dateStr.split("-");
  return new Date(year, month - 1, day);
}

export function getHotelFilters(destinationList: any, startDateFromLLM: string, hotelName?: string) {
  let startDate = startDateFromLLM;
  if (!startDateFromLLM || startDateFromLLM === "" || !isValidFutureDate(startDateFromLLM)) {
    startDate = getNextMonthDate();
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const hotelFilters: any[] = [];

  // Iterate through destination list
  destinationList?.forEach((destination: any) => {
    destination?.tripDetails?.forEach((tripDay: any) => {
      const dayDate = tripDay?.date;

      // Match any accommodation activity — hotel, large_hotel, resort,
      // apartment, bnb, boutique. The earlier exact-match list silently
      // dropped resort/apartment/bnb/boutique bookings, leaving the hotel
      // cities array empty, which gates the auto-fire of hotel search.
      const hotelActivities =
        tripDay?.itinerary_by_time_of_the_day_data?.filter((activity: any) =>
          typeof activity?.activity_type === "string" &&
          activity.activity_type.toLowerCase().startsWith("accommodation.")
        ) || [];

      hotelActivities.forEach((hotel: any) => {
        // Determine check-in and check-out
        const checkInRaw =
          hotel?.accommodation_checkin_date && parseDate(hotel.accommodation_checkin_date) >= today
            ? hotel.accommodation_checkin_date
            : dayDate || startDate;

        const checkOutRaw =
          hotel?.accommodation_checkout_date && parseDate(hotel.accommodation_checkout_date) >= today
            ? hotel.accommodation_checkout_date
            : getNextDateYYYYMMDD(checkInRaw, Number(hotel?.accommodation_number_of_nights ?? 1));

        // parseDate helper ensures proper Date object comparison
        const checkInDateObj = parseDate(checkInRaw);
        const checkOutDateObj = parseDate(checkOutRaw);

        hotelFilters.push({
          id: hotel?.id || "",
          type: "hotel",
          description: hotel?.description || "",
          activity_type: hotel?.activity_type,
          time_of_the_day: hotel?.time_of_the_day || "",
          accommodation_location_city: hotel?.accommodation_location_city || "",
          accommodation_location_name: hotel?.accommodation_location_name || "",
          accommodation_number_of_nights: hotel?.accommodation_number_of_nights || "",
          accommodation_location_latitude: hotel?.accommodation_location_latitude || "",
          accommodation_location_longitude: hotel?.accommodation_location_longitude || "",
          accommodation_location_country_code: hotel?.accommodation_location_country_code || "",
          checkIn: checkInDateObj < today ? "" : checkInRaw,
          checkOut: checkOutDateObj < today ? "" : checkOutRaw,
          tempcheckIn: checkInRaw,
          tempcheckOut: checkOutRaw,
          country: hotel?.accommodation_location_country_code || "",
          city: hotel?.accommodation_location_city || "",
          latitude: hotel?.accommodation_location_latitude || "",
          longitude: hotel?.accommodation_location_longitude || "",
          arrivalAddress: [hotel?.accommodation_location_city, hotel?.accommodation_location_country_code]
            .filter(Boolean)
            .join(", "),
          arrivalAirportCode: "",
          cityNotAvailable: false,
          isSelected: true,
          hotelName: hotelName || "",
        });
      });
    });
  });

  // If no hotels found, return default object
  if (hotelFilters.length === 0) {
    return [
      {
        id: "",
        type: "",
        description: "",
        activity_type: "",
        time_of_the_day: "",
        accommodation_location_city: "",
        accommodation_location_name: "",
        accommodation_number_of_nights: "",
        accommodation_location_latitude: "",
        accommodation_location_longitude: "",
        accommodation_location_country_code: "",
        checkIn: "",
        checkOut: "",
        country: "",
        city: "",
        latitude: "",
        longitude: "",
        arrivalAddress: "",
        arrivalAirportCode: "",
        cityNotAvailable: false,
        isSelected: true,
        showOnlyInputField: true,
      },
    ];
  }

  return hotelFilters;
}

// function getFlattenedItineraryByTimeOfDayData(tripData: any) {
//   return tripData?.flatMap((city: any) =>
//     city?.tripDetails?.flatMap((day: any) => day?.itinerary_by_time_of_the_day_data)
//   );
// }

// function findMatchingCity(data: any, cityName: string) {
//   return data?.filter((ele: any) => ele?.arrival_city_name?.split(" ")[0] === cityName?.split(" ")[0]);
// }

export function updateHotelFiltersBasedOnSelectedAirport(setBookingDetails: any) {
  setBookingDetails((prev: any) => {
    consoleLog(["UPDATE ARRIVAL AIRPORT", updateArrivalAirportCode(prev.hotels, prev.airPort)]);
    return {
      ...prev,
      hotels: updateArrivalAirportCode(prev.hotels, prev.airPort),
    };
  });
}

function updateArrivalAirportCode(hotelsArray: any, flightArray: any) {
  return hotelsArray.map((eleHotelsArray: any) => {
    const matchingFlight = flightArray.find(
      (flightEle: any) => flightEle?.arrivalAddress === eleHotelsArray?.arrivalAddress
    );
    consoleLog(["UPDATE ARRIVAL AIRPORT MATCHING", matchingFlight]);
    if (matchingFlight) {
      return { ...eleHotelsArray, arrivalAirportCode: matchingFlight?.arrival };
    }

    return eleHotelsArray;
  });
}

export function updateHotelCheckin(hotelsArray: any, flightDetailsArray: any) {
  consoleLog(["update Hotels dates", flightDetailsArray, hotelsArray]);
  let startDate = flightDetailsArray[0]?.segments?.at(-1)?.arrivalDate ?? new Date().toISOString().slice(0, 19);
  let updatedHotelsArray = hotelsArray?.map((eleHotelArray: any) => {
    const checkInDate = startDate;
    const checkoutDate = getNextDateYYYYMMDD(checkInDate, Number(eleHotelArray?.accommodation_number_of_nights ?? "1"));
    startDate = checkoutDate;

    return {
      ...eleHotelArray,
      checkIn: checkInDate?.split("T")[0],
      checkOut:
        checkoutDate?.split("T")[0] === checkInDate?.split("T")[0]
          ? getNextDateYYYYMMDD(checkInDate, 1)
          : checkoutDate?.split("T")[0],
    };
  });
  consoleLog(["UPDATED HOTELS FILTER 1", updatedHotelsArray]);
  updatedHotelsArray = updatedHotelsArray?.map((eleHotelsArray: any) => {
    const matchingArrivalFlight = flightDetailsArray?.find(
      (flightEle: any) =>
        flightEle?.segments[flightEle?.segments?.length - 1]?.arrAirportCode === eleHotelsArray?.arrivalAirportCode
    );
    const matchingDepartureFlight = flightDetailsArray?.find(
      (flightEle: any) => flightEle?.segments[0]?.depAirportCode === eleHotelsArray?.arrivalAirportCode
    );
    consoleLog(["CHECK IN FLIGHT", matchingArrivalFlight]);
    let checkIn = "";
    let checkOut = "";
    if (matchingArrivalFlight)
      checkIn =
        matchingArrivalFlight?.segments[matchingArrivalFlight?.segments?.length - 1]?.arrivalDate.split("T")[0] ?? "";
    if (matchingDepartureFlight) checkOut = matchingDepartureFlight?.segments[0]?.departureDate?.split("T")[0] ?? "";

    if (matchingArrivalFlight) {
      const tempCheckIn = checkIn !== "" ? checkIn : eleHotelsArray?.checkIn ?? "";
      const tempCheckOut =
        checkOut !== ""
          ? checkOut === checkIn
            ? getNextDateYYYYMMDD(checkIn, 1)
            : checkOut
          : eleHotelsArray?.checkOut ?? "";
      return {
        ...eleHotelsArray,
        checkIn: tempCheckIn,
        checkOut: isCheckOutAfterCheckIn(tempCheckIn, tempCheckOut) ? tempCheckOut : eleHotelsArray?.checkOut,
      };
    }

    return eleHotelsArray;
  });
  consoleLog(["UPDATED HOTELS FILTER 2", updatedHotelsArray]);
  return updatedHotelsArray;
}

function isCheckOutAfterCheckIn(checkIn: any, checkOut: any) {
  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);

  return checkOutDate > checkInDate;
}
